import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Send, 
  Loader2, 
  Lightbulb,
  CheckCircle2,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  MessageSquare,
  Phone,
  Sparkles,
  GraduationCap,
  ThumbsUp,
  ThumbsDown,
  Atom,
  Map,
  Brain,
  ClipboardCheck
} from 'lucide-react';
import { LEARN_ASSIST_MODES } from './LearnAssistModes';
import { AgentToolRail, LectureAiChip } from './design-kit';

// Audio constants
const SAMPLE_RATE = 24000;

// Helper to resample audio
const resampleAudio = (inputData, inputSampleRate, outputSampleRate) => {
  if (inputSampleRate === outputSampleRate) return inputData;
  
  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.round(inputData.length / ratio);
  const output = new Float32Array(outputLength);
  
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const srcIndexFloor = Math.floor(srcIndex);
    const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
    const t = srcIndex - srcIndexFloor;
    output[i] = inputData[srcIndexFloor] * (1 - t) + inputData[srcIndexCeil] * t;
  }
  
  return output;
};

const getLatestMessageContent = (messages, role) =>
  [...messages].reverse().find((message) => message.role === role && message.content)?.content || '';

function formatAgentToolMessage(tool, result) {
  const data = result?.data || {};

  if (tool.id === 'search_memory') {
    const matches = data.matches || [];
    if (!matches.length) {
      return 'No relevant memory was found yet.';
    }
    return `Pulled ${matches.length} memory match${matches.length === 1 ? '' : 'es'} into view for this session.`;
  }

  if (tool.id === 'save_note_summary' && data.note) {
    return `Saved "${data.note.title}" to Notes.`;
  }

  return result?.message || `${tool.label} completed.`;
}

export default function ConfusedModal({ 
  isOpen, 
  onClose, 
  lectureContent,
  lectureId = null,
  subjectId = null,
  onNoteSuggestion,
  defaultMode = 'text',
  autoUpdateNotes = false,
  assistantMode = 'tutor'
}) {
  // Connection state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [noteSuggestion, setNoteSuggestion] = useState(null);
  
  // Mode state - initialize from localStorage or default
  const [mode, setMode] = useState(() => defaultMode);
  const [showVoiceSetup, setShowVoiceSetup] = useState(() => {
    const savedVoice = localStorage.getItem('lectureai_voice');
    return defaultMode === 'voice' && !savedVoice;
  });
  
  // Learn-assist state
  const [activeTab, setActiveTab] = useState('tutor'); // 'tutor' or 'learn-assist'
  const [isLearnAssistLoading, setIsLearnAssistLoading] = useState(false);
  const [learnAssistMessages, setLearnAssistMessages] = useState([]);
  
  // Confusion tracking state
  const [currentConfusionLogId, setCurrentConfusionLogId] = useState(null);
  const [showFeedbackFor, setShowFeedbackFor] = useState(null); // Message index to show feedback for
  
  // Voice state - initialize from localStorage
  const [voices, setVoices] = useState([]);
  const [agentTools, setAgentTools] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem('lectureai_voice') || 'alloy';
  });
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [userTranscript, setUserTranscript] = useState('');
  const [aiTranscript, setAiTranscript] = useState('');
  const [micPermission, setMicPermission] = useState(null);
  
  // Refs
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const voicesLoadedRef = useRef(false);

  // Define playAudioQueue first (before it's used)
  const playAudioQueue = useCallback(async () => {
    if (audioQueueRef.current.length === 0 || !audioContextRef.current) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    
    while (audioQueueRef.current.length > 0) {
      const base64Audio = audioQueueRef.current.shift();
      try {
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const pcm16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
          float32[i] = pcm16[i] / 32768;
        }
        
        const audioBuffer = audioContextRef.current.createBuffer(1, float32.length, SAMPLE_RATE);
        audioBuffer.getChannelData(0).set(float32);
        
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);
        
        await new Promise(resolve => {
          source.onended = resolve;
          source.start();
        });
      } catch (e) {
        console.error('Audio playback error:', e);
      }
    }
    
    isPlayingRef.current = false;
  }, []);

  // Define handleRealtimeMessage (uses playAudioQueue)
  const handleRealtimeMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        setIsConnected(true);
        setIsConnecting(false);
        setMessages([{
          role: 'assistant',
          content: "Hi! I'm here to help you understand your lecture better. What's confusing you?"
        }]);
        break;
      case 'speech_started':
        setIsListening(true);
        setUserTranscript('');
        break;
      case 'speech_stopped':
        setIsListening(false);
        break;
      case 'user_transcript':
        setUserTranscript(data.transcript);
        setMessages(prev => [...prev, { role: 'user', content: data.transcript }]);
        break;
      case 'text_delta':
        setCurrentResponse(prev => prev + data.delta);
        break;
      case 'text_done':
        setMessages(prev => {
          const newMessages = [...prev, { role: 'assistant', content: data.text }];
          // Show feedback prompt for the new message
          setShowFeedbackFor(newMessages.length - 1);
          return newMessages;
        });
        setCurrentResponse('');
        
        // Update confusion log with resolution
        if (currentConfusionLogId) {
          api.updateConfusionLog(currentConfusionLogId, {
            resolution_text: data.text,
            resolved: true
          }).catch(err => console.error('Failed to update confusion log:', err));
        }
        break;
      case 'audio_delta':
        if (data.audio) {
          audioQueueRef.current.push(data.audio);
          if (!isPlayingRef.current) {
            playAudioQueue();
          }
        }
        setIsSpeaking(true);
        break;
      case 'audio_done':
        setIsSpeaking(false);
        break;
      case 'ai_transcript_delta':
        setAiTranscript(prev => prev + data.delta);
        break;
      case 'ai_transcript_done':
        setAiTranscript('');
        if (data.transcript) {
          setMessages(prev => [...prev, { role: 'assistant', content: data.transcript }]);
        }
        break;
      case 'response_done':
        setCurrentResponse(prev => {
          if (prev) {
            setMessages(msgs => [...msgs, { role: 'assistant', content: prev }]);
          }
          return '';
        });
        break;
      case 'note_suggestion':
        setNoteSuggestion(data.suggestion);
        if (autoUpdateNotes && onNoteSuggestion) {
          onNoteSuggestion(data.suggestion);
          toast.success('Notes updated with clarification');
        }
        break;
      case 'voice_updated':
        toast.success(`Voice changed to ${data.voice}`);
        break;
      case 'error':
        toast.error(data.message || 'An error occurred');
        break;
      default:
        break;
    }
  }, [autoUpdateNotes, currentConfusionLogId, onNoteSuggestion, playAudioQueue]);

  // Audio functions
  const initAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE
      });
    }
    
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
  }, []);

  const stopMicrophone = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.source.disconnect();
      processorRef.current.processor.disconnect();
      processorRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const startMicrophone = useCallback(async () => {
    try {
      await initAudioContext();
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      
      mediaStreamRef.current = stream;
      setMicPermission(true);
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        const resampledData = resampleAudio(inputData, audioContextRef.current.sampleRate, SAMPLE_RATE);
        
        const pcm16 = new Int16Array(resampledData.length);
        for (let i = 0; i < resampledData.length; i++) {
          const s = Math.max(-1, Math.min(1, resampledData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        const base64 = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));
        wsRef.current.send(JSON.stringify({ type: 'audio', audio: base64 }));
      };
      
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      processorRef.current = { source, processor };
      
      return true;
    } catch (error) {
      console.error('Microphone error:', error);
      setMicPermission(false);
      toast.error('Microphone access denied. Please enable microphone permission.');
      return false;
    }
  }, [initAudioContext]);

  const disconnectFromRealtime = useCallback(() => {
    stopMicrophone();
    if (wsRef.current) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'close' }));
        wsRef.current.close();
      } catch (e) {
        // Ignore errors when closing
      }
      wsRef.current = null;
    }
    setIsConnected(false);
    setMessages([]);
    setCurrentResponse('');
    setIsListening(false);
    setIsSpeaking(false);
    audioQueueRef.current = [];
  }, [stopMicrophone]);

  // Load voices - only once on mount, not using setState in effect
  useEffect(() => {
    if (voicesLoadedRef.current) return;
    voicesLoadedRef.current = true;
    
    api.getVoices().then(data => {
      setVoices(data.voices || []);
    }).catch(err => {
      console.error('Failed to load voices:', err);
    });
    api.getAgentTools('voice').then(data => {
      setAgentTools(data.tools || []);
    }).catch(err => {
      console.error('Failed to load agent tools:', err);
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    
    const initConnection = async () => {
      if (!isOpen || wsRef.current) return;
      
      setIsConnecting(true);
      try {
        const token = localStorage.getItem('session_token');
        const wsUrl = `${api.getRealtimeWsUrl()}?token=${token}`;
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = async () => {
          if (mode === 'voice') {
            await initAudioContext();
            const micStarted = await startMicrophone();
            if (!micStarted && mounted) {
              setMode('text');
              toast.info('Switched to text mode due to microphone access');
            }
          }
          
          ws.send(JSON.stringify({
            lecture_content: lectureContent,
            confusion_context: '',
            mode: mode,
            voice: selectedVoice,
            subject_id: subjectId,
            assistant_mode: assistantMode
          }));
        };

        ws.onmessage = (event) => {
          if (!mounted) return;
          const data = JSON.parse(event.data);
          handleRealtimeMessage(data);
        };

        ws.onerror = () => {
          if (!mounted) return;
          toast.error('Connection error');
          setIsConnected(false);
          setIsConnecting(false);
        };

        ws.onclose = () => {
          if (!mounted) return;
          setIsConnected(false);
          wsRef.current = null;
          stopMicrophone();
        };
      } catch (error) {
        console.error('Failed to connect:', error);
        if (mounted) {
          toast.error('Failed to connect to AI tutor');
          setIsConnecting(false);
        }
      }
    };

    if (isOpen && !showVoiceSetup) {
      initConnection();
    }

    return () => {
      mounted = false;
      disconnectFromRealtime();
    };
  }, [isOpen, showVoiceSetup, lectureContent, mode, selectedVoice, subjectId, assistantMode, handleRealtimeMessage, disconnectFromRealtime, initAudioContext, startMicrophone, stopMicrophone]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, currentResponse, aiTranscript]);

  // Handlers
  const sendMessage = async () => {
    if (!inputText.trim() || !wsRef.current || !isConnected) return;

    const userMessage = inputText;
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    wsRef.current.send(JSON.stringify({ type: 'text', text: userMessage }));
    setInputText('');
    
    // Log confusion interaction for analytics
    try {
      const confusionLog = await api.createConfusionLog({
        lecture_id: lectureId,
        subject_id: subjectId,
        confusion_text: userMessage,
        source: 'confused_modal'
      });
      setCurrentConfusionLogId(confusionLog.id);
    } catch (error) {
      console.error('Failed to log confusion:', error);
      // Don't interrupt the user experience for analytics failures
    }
  };

  const applyAgentTool = async (tool) => {
    if (!tool) return;

    const latestUserMessage = getLatestMessageContent(messages, 'user');
    const latestAssistantMessage = currentResponse || aiTranscript || getLatestMessageContent(messages, 'assistant');
    const draftText = inputText.trim() || userTranscript.trim();

    if (tool.id === 'search_memory' && !draftText && !latestUserMessage) {
      setInputText(tool.prompt_hint || '');
      toast.info('Add the concept you want to search first.');
      return;
    }

    if ((tool.id === 'log_confusion_point' || tool.id === 'create_revisit_task') && !draftText && !latestUserMessage) {
      setInputText(tool.prompt_hint || '');
      toast.info('Describe the confusing topic first.');
      return;
    }

    if (tool.id === 'save_note_summary' && !latestAssistantMessage) {
      toast.info('Wait for a tutor explanation before saving to notes.');
      return;
    }

    try {
      const payload = {
        subject_id: subjectId,
        lecture_id: lectureId,
        source: 'confused_modal_agent_tool',
      };

      if (tool.id === 'search_memory') {
        payload.query = draftText || latestUserMessage;
      }

      if (tool.id === 'log_confusion_point') {
        payload.confusion_text = draftText || latestUserMessage;
      }

      if (tool.id === 'create_revisit_task') {
        payload.topic = draftText || latestUserMessage;
        payload.notes = latestAssistantMessage || null;
      }

      if (tool.id === 'save_note_summary') {
        payload.content = latestAssistantMessage;
        payload.title = assistantMode === 'reviewer' ? 'Honest coach note' : 'Voice tutor note';
      }

      const response = await api.executeAgentTool(tool.id, payload, 'voice');
      const result = response.result;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: formatAgentToolMessage(tool, result),
          isSystemMessage: true,
          toolResult: response,
        }
      ]);
      toast.success(result?.message || `${tool.label} complete`);
    } catch (error) {
      console.error('Agent tool failed:', error);
      toast.error(error.message || `Failed to run ${tool.label}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleApplySuggestion = () => {
    if (noteSuggestion && onNoteSuggestion) {
      onNoteSuggestion(noteSuggestion);
      toast.success('Notes updated with clarification');
      setNoteSuggestion(null);
    }
  };

  const handleVoiceChange = (newVoice) => {
    setSelectedVoice(newVoice);
    localStorage.setItem('lectureai_voice', newVoice);
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'update_voice', voice: newVoice }));
    }
  };

  const handleModeSwitch = async (newMode) => {
    if (newMode === mode) return;
    
    if (newMode === 'voice') {
      const micStarted = await startMicrophone();
      if (!micStarted) {
        toast.error('Cannot switch to voice mode - microphone access denied');
        return;
      }
    } else {
      stopMicrophone();
    }
    
    setMode(newMode);
  };

  const handleClose = () => {
    disconnectFromRealtime();
    onClose();
  };

  const startVoiceSession = () => {
    localStorage.setItem('lectureai_voice', selectedVoice);
    setShowVoiceSetup(false);
  };

  // Learn-assist handlers
  const handleLearnAssistMessage = async (prompt, toolId) => {
    setIsLearnAssistLoading(true);
    setLearnAssistMessages(prev => [...prev, { role: 'user', content: prompt, toolId }]);
    
    try {
      // Use the chat API with the Learn-assist context
      const result = await api.chat(
        [{ role: 'user', content: prompt }],
        'gpt-4o-mini',
        null, // No single lecture
        [], // No docs
        [], // No URLs
        [] // Let the prompt include lecture context
      );
      
      setLearnAssistMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
    } catch (error) {
      toast.error(error.message || 'Failed to get response');
    } finally {
      setIsLearnAssistLoading(false);
    }
  };

  const handleClearLearnAssist = () => {
    setLearnAssistMessages([]);
  };

  // Handle feedback on AI response (was it helpful?)
  const handleFeedback = async (helpful) => {
    if (currentConfusionLogId) {
      try {
        await api.updateConfusionLog(currentConfusionLogId, {
          resolution_helpful: helpful
        });
        toast.success(helpful ? 'Glad that helped!' : 'Thanks for the feedback');
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    }
    setShowFeedbackFor(null);
    setCurrentConfusionLogId(null);
  };

  // Voice Setup Screen
  if (showVoiceSetup && isOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-primary" />
              Choose AI Voice
            </DialogTitle>
            <DialogDescription>
              Select a voice for your AI tutor. You can change this anytime.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              {voices.map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => setSelectedVoice(voice.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    selectedVoice === voice.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <p className="font-medium">{voice.name}</p>
                  <p className="text-xs text-muted-foreground">{voice.description}</p>
                </button>
              ))}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setMode('text');
                  setShowVoiceSetup(false);
                }}
                className="flex-1"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Use Text Instead
              </Button>
              <Button onClick={startVoiceSession} className="flex-1">
                <Phone className="w-4 h-4 mr-2" />
                Start Voice Chat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl h-[650px] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 font-serif">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              I&apos;m Confused - AI Tutor
            </DialogTitle>
            
            {/* Mode & Voice Controls - only show in tutor tab */}
            {activeTab === 'tutor' && (
              <div className="flex items-center gap-2">
                {/* Mode Toggle */}
                <div className="flex rounded-lg border overflow-hidden">
                  <button
                    onClick={() => handleModeSwitch('text')}
                    className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                      mode === 'text' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Text
                  </button>
                  <button
                    onClick={() => handleModeSwitch('voice')}
                    className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                      mode === 'voice' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    <Mic className="w-3.5 h-3.5" />
                    Voice
                  </button>
                </div>

                {/* Voice Selector */}
                {mode === 'voice' && (
                  <Select value={selectedVoice} onValueChange={handleVoiceChange}>
                    <SelectTrigger className="w-28 h-8">
                      <Volume2 className="w-3.5 h-3.5 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          {voice.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogDescription>
            {activeTab === 'tutor' 
              ? (mode === 'voice' 
                ? 'Speak naturally - I\'ll listen and respond with voice'
                : 'Ask questions about your lecture and get instant clarification')
              : 'Use proven learning techniques to master your material'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="px-6 pt-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full h-10 grid grid-cols-2">
              <TabsTrigger value="tutor" className="gap-2">
                <MessageSquare className="w-4 h-4" />
                Ask Tutor
              </TabsTrigger>
              <TabsTrigger value="learn-assist" className="gap-2">
                <GraduationCap className="w-4 h-4" />
                Learn-assist™
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {activeTab === 'tutor' ? (
          <>
            {/* Voice Status Indicator */}
            {mode === 'voice' && isConnected && (
              <div className="px-6 py-2 bg-muted/50 flex items-center justify-center gap-4">
                <div className={`flex items-center gap-2 ${isListening ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {isListening ? <Mic className="w-4 h-4 animate-pulse" /> : <MicOff className="w-4 h-4" />}
                  <span className="text-sm">{isListening ? 'Listening...' : 'Waiting for speech'}</span>
                </div>
                <div className={`flex items-center gap-2 ${isSpeaking ? 'text-blue-500' : 'text-muted-foreground'}`}>
                  {isSpeaking ? <Volume2 className="w-4 h-4 animate-pulse" /> : <VolumeX className="w-4 h-4" />}
                  <span className="text-sm">{isSpeaking ? 'Speaking...' : ''}</span>
                </div>
              </div>
            )}

            {/* Messages Area */}
            <ScrollArea ref={scrollRef} className="flex-1 px-6 py-4">
              {isConnecting ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-muted-foreground">Connecting to AI tutor...</p>
                </div>
              ) : !isConnected ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <p className="text-muted-foreground">Connection lost</p>
                  <Button onClick={() => { handleClose(); onClose(); }}>Close and Try Again</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className="max-w-[80%]">
                        <div
                          className={`rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        
                        {/* Feedback buttons for assistant messages */}
                        {msg.role === 'assistant' && showFeedbackFor === idx && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span>Was this helpful?</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 hover:text-green-500 hover:bg-green-500/10"
                              onClick={() => handleFeedback(true)}
                            >
                              <ThumbsUp className="w-3.5 h-3.5 mr-1" />
                              Yes
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 hover:text-red-500 hover:bg-red-500/10"
                              onClick={() => handleFeedback(false)}
                            >
                              <ThumbsDown className="w-3.5 h-3.5 mr-1" />
                              No
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Live user transcript (voice mode) */}
                  {mode === 'voice' && userTranscript && (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-primary/50 text-primary-foreground">
                        <p className="text-sm whitespace-pre-wrap italic">{userTranscript}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Streaming response / AI transcript */}
                  {(currentResponse || aiTranscript) && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-muted">
                        <p className="text-sm whitespace-pre-wrap">{currentResponse || aiTranscript}</p>
                        <span className="inline-block w-2 h-4 bg-foreground/50 animate-pulse ml-1" />
                      </div>
                    </div>
                  )}

                  {/* Note Suggestion */}
                  {noteSuggestion && !autoUpdateNotes && (
                    <div className="border border-amber-500/50 bg-amber-500/5 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-sm mb-2">Suggested Note Update</p>
                          <p className="text-sm text-muted-foreground mb-3">{noteSuggestion}</p>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleApplySuggestion}>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Apply to Notes
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setNoteSuggestion(null)}>
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="px-6 py-4 border-t">
              <div className="mb-3">
                <AgentToolRail tools={agentTools} onToolClick={applyAgentTool} title="Allowlisted voice tools" />
              </div>
              {mode === 'text' ? (
                <>
                  {/* Quick mode chips */}
                  <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                    <button
                      onClick={() => setInputText('Explain this from first principles: ')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30 text-violet-600 hover:border-violet-500/50 transition-colors whitespace-nowrap"
                    >
                      <Atom className="w-3 h-3" />
                      First-Principles
                    </button>
                    <button
                      onClick={() => setInputText('Give me the big picture of: ')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-blue-500/30 text-blue-600 hover:border-blue-500/50 transition-colors whitespace-nowrap"
                    >
                      <Map className="w-3 h-3" />
                      Big Picture
                    </button>
                    <button
                      onClick={() => setInputText('Help me memorize: ')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/30 text-pink-600 hover:border-pink-500/50 transition-colors whitespace-nowrap"
                    >
                      <Brain className="w-3 h-3" />
                      Memory
                    </button>
                    <button
                      onClick={() => setInputText('Quiz me on: ')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gradient-to-r from-indigo-500/10 to-violet-500/10 border border-indigo-500/30 text-indigo-600 hover:border-indigo-500/50 transition-colors whitespace-nowrap"
                    >
                      <ClipboardCheck className="w-3 h-3" />
                      Quiz Me
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your question or select a mode above..."
                      disabled={!isConnected}
                      className="flex-1"
                      data-testid="confused-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!isConnected || !inputText.trim()}
                      data-testid="confused-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Tip: Use the mode chips for structured learning approaches
                  </p>
                </>
              ) : (
                <div className="text-center">
                  <div className="mb-3 flex flex-wrap justify-center gap-2">
                    <LectureAiChip icon={Mic} label="Voice tutor live" tone="subject" active />
                    <LectureAiChip icon={assistantMode === 'reviewer' ? ClipboardCheck : MessageSquare} label={assistantMode === 'reviewer' ? 'Honest coach' : 'Tutor'} tone="tool" active />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {micPermission === false 
                      ? 'Microphone access denied. Please enable it in your browser settings.'
                      : 'Speak naturally - the AI will respond with voice. Switch to Text mode to type instead.'
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Learn-assist™ Tab Content */
          <div className="flex-1 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 px-6 py-4">
              {/* Learn-assist response messages */}
              {learnAssistMessages.length > 0 ? (
                <div className="space-y-4 mb-4">
                  {learnAssistMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[90%] rounded-2xl px-4 py-3 ${
                          msg.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  {isLearnAssistLoading && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl px-4 py-3 bg-muted">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-muted-foreground">Generating response...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
              
              {/* Learn-assist tools */}
              <LearnAssistTools
                onSendMessage={handleLearnAssistMessage}
                lectureContext={lectureContent}
                isLoading={isLearnAssistLoading}
                messages={learnAssistMessages}
                compact={true}
              />
            </ScrollArea>
            
            {learnAssistMessages.length > 0 && (
              <div className="px-6 py-3 border-t">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearLearnAssist}
                  className="w-full"
                >
                  Clear & Start Fresh
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
